import { supabase } from "../supabaseClient";
import { dbError } from "./toast";

const isUUID = (id) => typeof id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

// Wraps an async db operation to catch network-level errors (offline, timeout)
const safe = async (operation, entity, fn) => {
  try {
    return await fn();
  } catch (e) {
    dbError(operation, entity, e);
    return null;
  }
};

// ============================================================
// CLASSROOMS
// ============================================================
export const fetchClassrooms = async (schoolId) => {
  const { data, error } = await supabase.from("classrooms").select("*").eq("school_id", schoolId).order("name");
  if (error) dbError("fetch", "classrooms", error);
  return (data || []).map(mapClassroomRow);
};

const mapClassroomRow = (c) => ({
  id: c.id, name: c.name, ageGroup: c.age_group, room: c.room || "",
  capacity: c.capacity || 20, staffRatio: c.ratio || 2, color: c.color,
  tuitionPlanId: c.tuition_plan_id || null,
  school_id: c.school_id
});

export const upsertClassroom = async (classroom, schoolId) => safe("save", "classroom", async () => {
  const row = {
    school_id: schoolId,
    name: classroom.name,
    age_group: classroom.ageGroup,
    room: classroom.room || "",
    capacity: classroom.capacity || 20,
    ratio: classroom.staffRatio || 2,
    color: classroom.color || null,
    tuition_plan_id: classroom.tuitionPlanId || null
  };
  if (isUUID(classroom.id)) row.id = classroom.id;
  const { data, error } = await supabase.from("classrooms").upsert(row).select().single();
  if (error) dbError("save", "classroom", error);
  return data ? mapClassroomRow(data) : null;
});

export const deleteClassroom = async (id) => safe("delete", "classroom", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("classrooms").delete().eq("id", id);
  if (error) dbError("delete", "classroom", error);
  return !error;
});

// ============================================================
// STUDENTS
// ============================================================
export const fetchStudents = async (schoolId) => {
  const { data, error } = await supabase.from("students").select("*").eq("school_id", schoolId).order("last_name");
  if (error) dbError("fetch", "students", error);
  return (data || []).map(mapStudentRow);
};

const mapStudentRow = (s) => ({
  id: s.id, firstName: s.first_name, middleName: s.middle_name || "", lastName: s.last_name,
  dob: s.date_of_birth || "", gender: s.gender || "", classroomId: s.classroom_id,
  allergies: (s.allergies || []).join(", "), notes: s.notes || "", photo: s.photo_url || null,
  address: s.address || "", status: s.status || "active",
  guardianName: s.guardian_name || "", guardianPhone: s.guardian_phone || "",
  guardianEmail: s.guardian_email || "", guardianRelation: s.guardian_relation || "Parent",
  guardianEmployer: s.guardian_employer || "", guardianWorkPhone: s.guardian_work_phone || "",
  parent2Name: s.parent2_name || "", parent2Relation: s.parent2_relation || "",
  parent2Phone: s.parent2_phone || "", parent2Email: s.parent2_email || "",
  parent2Employer: s.parent2_employer || "", parent2WorkPhone: s.parent2_work_phone || "",
  physician: s.physician || "", physicianPhone: s.physician_phone || "",
  insuranceProvider: s.insurance_provider || "", insurancePolicy: s.insurance_policy || "",
  medications: s.medications || "", specialNeeds: s.special_needs || "",
  emergencyContact: s.emergency_contact || "", emergencyPhone: s.emergency_phone || "",
  authorizedPickup: s.authorized_pickup || [],
  enrollmentDate: s.enrollment_date || "",
  school_id: s.school_id
});

export const upsertStudent = async (student, schoolId) => safe("save", "student", async () => {
  const row = {
    school_id: schoolId,
    classroom_id: isUUID(student.classroomId) ? student.classroomId : null,
    first_name: student.firstName,
    middle_name: student.middleName || null,
    last_name: student.lastName,
    date_of_birth: student.dob || null,
    gender: student.gender || null,
    address: student.address || null,
    photo_url: student.photo || null,
    allergies: student.allergies ? student.allergies.split(",").map(a => a.trim()).filter(Boolean) : [],
    notes: student.notes || null,
    status: student.status || "active",
    guardian_name: student.guardianName || null,
    guardian_phone: student.guardianPhone || null,
    guardian_email: student.guardianEmail || null,
    guardian_relation: student.guardianRelation || "Parent",
    guardian_employer: student.guardianEmployer || null,
    guardian_work_phone: student.guardianWorkPhone || null,
    parent2_name: student.parent2Name || null,
    parent2_relation: student.parent2Relation || null,
    parent2_phone: student.parent2Phone || null,
    parent2_email: student.parent2Email || null,
    parent2_employer: student.parent2Employer || null,
    parent2_work_phone: student.parent2WorkPhone || null,
    physician: student.physician || null,
    physician_phone: student.physicianPhone || null,
    insurance_provider: student.insuranceProvider || null,
    insurance_policy: student.insurancePolicy || null,
    medications: student.medications || null,
    special_needs: student.specialNeeds || null,
    emergency_contact: student.emergencyContact || null,
    emergency_phone: student.emergencyPhone || null,
    authorized_pickup: student.authorizedPickup || [],
    enrollment_date: student.enrollmentDate || null
  };
  if (isUUID(student.id)) row.id = student.id;
  const { data, error } = await supabase.from("students").upsert(row).select().single();
  if (error) dbError("save", "student", error);
  return data ? mapStudentRow(data) : null;
});

export const deleteStudent = async (id) => safe("delete", "student", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("students").delete().eq("id", id);
  if (error) dbError("delete", "student", error);
  return !error;
});

// ============================================================
// TEACHERS
// ============================================================
export const fetchTeachers = async (schoolId) => {
  const { data, error } = await supabase.from("teachers").select("*").eq("school_id", schoolId).order("name");
  if (error) dbError("fetch", "teachers", error);
  return (data || []).map(mapTeacherRow);
};

const mapTeacherRow = (t) => ({
  id: t.id, name: t.name, email: t.email || "", phone: t.phone || "",
  extension: t.extension || "",
  role: t.role || "Lead Teacher", assignedRoom: t.assigned_room || null,
  isSub: t.is_sub || false, status: t.status || "active", avatar: t.avatar || null,
  school_id: t.school_id
});

export const upsertTeacher = async (teacher, schoolId) => safe("save", "teacher", async () => {
  const row = {
    school_id: schoolId,
    name: teacher.name,
    email: teacher.email || null,
    phone: teacher.phone || null,
    extension: teacher.extension || null,
    role: teacher.role || "Lead Teacher",
    assigned_room: isUUID(teacher.assignedRoom) ? teacher.assignedRoom : null,
    is_sub: teacher.isSub || false,
    status: teacher.status || "active",
    avatar: teacher.avatar || null
  };
  if (isUUID(teacher.id)) row.id = teacher.id;
  const { data, error } = await supabase.from("teachers").upsert(row).select().single();
  if (error) dbError("save", "teacher", error);
  // Also sync assigned_room to the profiles table so the teacher sees their room on login
  if (teacher.email && teacher.assignedRoom !== undefined) {
    const assignedRoomVal = isUUID(teacher.assignedRoom) ? teacher.assignedRoom : null;
    await supabase.from("profiles")
      .update({ assigned_room: assignedRoomVal, role: teacher.role || "teacher" })
      .eq("school_id", schoolId)
      .eq("email", teacher.email)
      .catch(() => {});
  }
  return data ? mapTeacherRow(data) : null;
});

export const deleteTeacher = async (id) => safe("delete", "teacher", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("teachers").delete().eq("id", id);
  if (error) dbError("delete", "teacher", error);
  return !error;
});

// ============================================================
// TICKETS
// ============================================================
export const fetchTickets = async (schoolId) => {
  const { data, error } = await supabase.from("tickets").select("*").eq("school_id", schoolId).order("created_at", { ascending: false });
  if (error) dbError("fetch", "tickets", error);
  return (data || []).map(t => ({
    id: t.id, title: t.title, description: t.description || "", category: t.category || "Maintenance",
    priority: t.priority || "medium", status: t.status || "open",
    reporter: t.reporter || t.reporter_name || "", assignee: t.assignee || t.assignee_name || "Unassigned",
    created: t.created_at, school_id: t.school_id
  }));
};

export const upsertTicket = async (ticket, schoolId) => safe("save", "ticket", async () => {
  const row = {
    school_id: schoolId, title: ticket.title, description: ticket.description || null,
    category: ticket.category || "Maintenance", priority: ticket.priority || "medium",
    status: ticket.status || "open", reporter: ticket.reporter || null,
    assignee: ticket.assignee || "Unassigned"
  };
  if (isUUID(ticket.id)) row.id = ticket.id;
  const { data, error } = await supabase.from("tickets").upsert(row).select().single();
  if (error) dbError("save", "ticket", error);
  return data ? { id: data.id, title: data.title, description: data.description || "", category: data.category, priority: data.priority, status: data.status, reporter: data.reporter || "", assignee: data.assignee || "Unassigned", created: data.created_at, school_id: data.school_id } : null;
});

export const deleteTicket = async (id) => safe("delete", "ticket", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("tickets").delete().eq("id", id);
  if (error) dbError("delete", "ticket", error);
  return !error;
});

// ============================================================
// EVENTS
// ============================================================
export const fetchEvents = async (schoolId) => {
  const { data, error } = await supabase.from("events").select("*").eq("school_id", schoolId).order("date");
  if (error) dbError("fetch", "events", error);
  return (data || []).map(e => ({
    id: e.id, title: e.title, description: e.description || "", date: e.date,
    time: e.time || "", timeStart: e.time_start || "", timeEnd: e.time_end || "",
    location: e.location || "", type: e.type || "academic", grades: e.grades || ["All"],
    school_id: e.school_id
  }));
};

export const upsertEvent = async (event, schoolId) => safe("save", "event", async () => {
  const row = {
    school_id: schoolId, title: event.title, description: event.description || null,
    date: event.date, time: event.time || null, time_start: event.timeStart || null,
    time_end: event.timeEnd || null, location: event.location || null,
    type: event.type || "academic", grades: event.grades || ["All"]
  };
  if (isUUID(event.id)) row.id = event.id;
  const { data, error } = await supabase.from("events").upsert(row).select().single();
  if (error) dbError("save", "event", error);
  return data ? { id: data.id, title: data.title, description: data.description || "", date: data.date, time: data.time || "", timeStart: data.time_start || "", timeEnd: data.time_end || "", location: data.location || "", type: data.type, grades: data.grades || ["All"], school_id: data.school_id } : null;
});

export const deleteEvent = async (id) => safe("delete", "event", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) dbError("delete", "event", error);
  return !error;
});

// ============================================================
// TUITION PLANS
// ============================================================
export const fetchTuitionPlans = async (schoolId) => {
  const { data, error } = await supabase.from("tuition_plans").select("*").eq("school_id", schoolId).order("name");
  if (error) dbError("fetch", "tuitionplans", error);
  return (data || []).map(p => ({
    id: p.id, name: p.name, monthlyRate: parseFloat(p.monthly_rate) || 0,
    registrationFee: parseFloat(p.registration_fee) || 0,
    siblingDiscount: parseFloat(p.sibling_discount) || 0,
    staffDiscount: parseFloat(p.staff_discount) || 0,
    notes: p.notes || "", school_id: p.school_id
  }));
};

export const upsertTuitionPlan = async (plan, schoolId) => safe("save", "tuition plan", async () => {
  const row = {
    school_id: schoolId, name: plan.name,
    monthly_rate: plan.monthlyRate || 0, registration_fee: plan.registrationFee || 0,
    sibling_discount: plan.siblingDiscount || 0, staff_discount: plan.staffDiscount || 0,
    notes: plan.notes || null
  };
  if (isUUID(plan.id)) row.id = plan.id;
  const { data, error } = await supabase.from("tuition_plans").upsert(row).select().single();
  if (error) dbError("save", "tuitionplan", error);
  return data ? { id: data.id, name: data.name, monthlyRate: parseFloat(data.monthly_rate) || 0, registrationFee: parseFloat(data.registration_fee) || 0, siblingDiscount: parseFloat(data.sibling_discount) || 0, staffDiscount: parseFloat(data.staff_discount) || 0, notes: data.notes || "", school_id: data.school_id } : null;
});

export const deleteTuitionPlan = async (id) => safe("delete", "tuition plan", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("tuition_plans").delete().eq("id", id);
  if (error) dbError("delete", "tuitionplan", error);
  return !error;
});

// ============================================================
// ENROLLMENT APPS (stored as JSONB for flexibility)
// ============================================================
export const fetchEnrollmentApps = async (schoolId) => {
  const { data, error } = await supabase.from("enrollment_apps").select("*").eq("school_id", schoolId).order("created_at", { ascending: false });
  if (error) dbError("fetch", "enrollmentapps", error);
  return (data || []).map(e => ({
    id: e.id, ...(e.form_data || {}),
    status: e.stage || "inquiry", school_id: e.school_id, createdAt: e.created_at || ""
  }));
};

export const upsertEnrollmentApp = async (app, schoolId) => safe("save", "enrollment application", async () => {
  const { id, school_id, ...formData } = app;
  const row = {
    school_id: schoolId,
    stage: app.status || "inquiry",
    child_first: app.childFirst || "",
    child_last: app.childLast || "",
    form_data: formData
  };
  if (isUUID(id)) row.id = id;
  const { data, error } = await supabase.from("enrollment_apps").upsert(row).select().single();
  if (error) dbError("save", "enrollmentapp", error);
  return data ? { id: data.id, ...(data.form_data || {}), status: data.stage, school_id: data.school_id } : null;
});

export const deleteEnrollmentApp = async (id) => safe("delete", "enrollment application", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("enrollment_apps").delete().eq("id", id);
  if (error) dbError("delete", "enrollmentapp", error);
  return !error;
});

// ============================================================
// INVOICES
// ============================================================
export const fetchInvoices = async (schoolId) => {
  const { data, error } = await supabase.from("invoices").select("*").eq("school_id", schoolId).order("due_date", { ascending: false });
  if (error) dbError("fetch", "invoices", error);
  return (data || []).map(i => ({
    id: i.id, studentId: i.student_id, planId: i.plan_id, amount: parseFloat(i.amount) || 0,
    status: i.status || "pending", dueDate: i.due_date, paidDate: i.paid_date,
    period: i.period || "", notes: i.notes || "", paymentMethod: i.payment_method || "",
    partialAmount: parseFloat(i.partial_amount) || 0, school_id: i.school_id
  }));
};

export const upsertInvoice = async (invoice, schoolId) => safe("save", "invoice", async () => {
  const row = {
    school_id: schoolId, student_id: isUUID(invoice.studentId) ? invoice.studentId : null,
    plan_id: isUUID(invoice.planId) ? invoice.planId : null,
    amount: invoice.amount || 0, status: invoice.status || "pending",
    due_date: invoice.dueDate, paid_date: invoice.paidDate || null,
    period: invoice.period || null, notes: invoice.notes || null,
    payment_method: invoice.paymentMethod || null,
    partial_amount: invoice.partialAmount || null
  };
  if (isUUID(invoice.id)) row.id = invoice.id;
  const { data, error } = await supabase.from("invoices").upsert(row).select().single();
  if (error) dbError("save", "invoice", error);
  return data ? { id: data.id, studentId: data.student_id, planId: data.plan_id, amount: parseFloat(data.amount), status: data.status, dueDate: data.due_date, paidDate: data.paid_date, period: data.period || "", notes: data.notes || "", paymentMethod: data.payment_method || "", partialAmount: parseFloat(data.partial_amount) || 0, school_id: data.school_id } : null;
});

export const deleteInvoice = async (id) => safe("delete", "invoice", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("invoices").delete().eq("id", id);
  if (error) dbError("delete", "invoice", error);
  return !error;
});

// ============================================================
// STUDENT ATTENDANCE
// App format: { "2026-03-01": { studentId: "present", studentId2: "absent:reason" } }
// DB format: individual rows per student per date
// ============================================================
export const fetchStudentAttendance = async (schoolId) => {
  const { data, error } = await supabase.from("student_attendance").select("*").eq("school_id", schoolId);
  if (error) dbError("fetch", "studentattendance", error);
  const result = {};
  (data || []).forEach(row => {
    if (!result[row.date]) result[row.date] = {};
    result[row.date][row.student_id] = row.status;
  });
  return result;
};

export const syncStudentAttendance = async (attendance, schoolId) => safe("sync", "student attendance", async () => {
  // Convert app format to rows and upsert
  const rows = [];
  Object.entries(attendance).forEach(([date, students]) => {
    Object.entries(students).forEach(([studentId, status]) => {
      if (isUUID(studentId)) {
        rows.push({ school_id: schoolId, student_id: studentId, date, status });
      }
    });
  });
  if (rows.length === 0) return;
  const { error } = await supabase.from("student_attendance").upsert(rows, { onConflict: "school_id,student_id,date" });
  if (error) dbError("sync", "studentattendance", error);
});

// ============================================================
// TEACHER ATTENDANCE
// Same pattern as student attendance
// ============================================================
export const fetchTeacherAttendance = async (schoolId) => {
  const { data, error } = await supabase.from("teacher_attendance").select("*").eq("school_id", schoolId);
  if (error) dbError("fetch", "teacherattendance", error);
  const result = {};
  (data || []).forEach(row => {
    if (!result[row.date]) result[row.date] = {};
    result[row.date][row.teacher_id] = row.status;
  });
  return result;
};

export const syncTeacherAttendance = async (attendance, schoolId) => safe("sync", "teacher attendance", async () => {
  const rows = [];
  Object.entries(attendance).forEach(([date, teachers]) => {
    Object.entries(teachers).forEach(([teacherId, status]) => {
      if (isUUID(teacherId)) {
        rows.push({ school_id: schoolId, teacher_id: teacherId, date, status });
      }
    });
  });
  if (rows.length === 0) return;
  const { error } = await supabase.from("teacher_attendance").upsert(rows, { onConflict: "school_id,teacher_id,date" });
  if (error) dbError("sync", "teacherattendance", error);
});

// ============================================================
// AUDIT LOG
// ============================================================
export const fetchAuditLog = async (schoolId) => {
  const { data, error } = await supabase.from("audit_log").select("*").eq("school_id", schoolId).order("created_at", { ascending: false }).limit(500);
  if (error) dbError("fetch", "auditlog", error);
  return (data || []).map(a => ({
    id: a.id, timestamp: a.created_at, userId: a.user_id, userName: a.user_name,
    role: a.role, action: a.action, detail: a.detail
  }));
};

export const insertAuditEntry = async (entry, schoolId) => {
  const { error } = await supabase.from("audit_log").insert({
    school_id: schoolId, user_id: isUUID(entry.userId) ? entry.userId : null,
    user_name: entry.userName, role: entry.role, action: entry.action, detail: entry.detail
  });
  if (error) console.error("insertAuditEntry:", error);
};

// ============================================================
// CHANNELS
// ============================================================
export const fetchChannels = async (schoolId) => {
  const { data, error } = await supabase.from("channels").select("*").eq("school_id", schoolId).order("created_at");
  if (error) dbError("fetch", "channels", error);
  return (data || []).map(c => ({
    id: c.id, name: c.name, icon: c.icon || "#", description: c.description || "",
    type: c.type || "public", members: c.members || [], createdBy: c.created_by,
    school_id: c.school_id
  }));
};

export const upsertChannel = async (channel, schoolId) => safe("save", "channel", async () => {
  const row = {
    school_id: schoolId, name: channel.name, icon: channel.icon || "#",
    description: channel.description || null, type: channel.type || "public",
    members: channel.members || [], created_by: channel.createdBy || null
  };
  if (isUUID(channel.id)) row.id = channel.id;
  const { data, error } = await supabase.from("channels").upsert(row).select().single();
  if (error) dbError("save", "channel", error);
  return data ? { id: data.id, name: data.name, icon: data.icon, description: data.description || "", type: data.type, members: data.members || [], createdBy: data.created_by, school_id: data.school_id } : null;
});

export const deleteChannel = async (id) => safe("delete", "channel", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("channels").delete().eq("id", id);
  if (error) dbError("delete", "channel", error);
  return !error;
});

// ============================================================
// MESSAGES
// ============================================================
export const fetchMessages = async (schoolId) => {
  const { data, error } = await supabase.from("messages").select("*").eq("school_id", schoolId).order("created_at");
  if (error) dbError("fetch", "messages", error);
  return (data || []).map(m => ({
    id: m.id, channelId: m.channel_id, senderId: m.sender_id, text: m.text,
    date: m.date, time: m.time || "", edited: m.edited || false,
    isSystem: m.is_system || false, school_id: m.school_id,
    createdAt: m.created_at || null
  }));
};

export const upsertMessage = async (message, schoolId) => safe("save", "message", async () => {
  const row = {
    school_id: schoolId, channel_id: String(message.channelId), sender_id: String(message.senderId),
    text: message.text, date: message.date || null, time: message.time || null,
    edited: message.edited || false, is_system: message.isSystem || false
  };
  let data, error;
  if (isUUID(message.id)) {
    row.id = message.id;
    ({ data, error } = await supabase.from("messages").upsert(row).select().single());
  } else {
    ({ data, error } = await supabase.from("messages").insert(row).select().single());
  }
  if (error) dbError("save", "message", error);
  return data ? { id: data.id, channelId: data.channel_id, senderId: data.sender_id, text: data.text, date: data.date, time: data.time || "", edited: data.edited, isSystem: data.is_system, school_id: data.school_id, createdAt: data.created_at || null } : null;
});

export const deleteMessage = async (id) => safe("delete", "message", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("messages").delete().eq("id", id);
  if (error) dbError("delete", "message", error);
  return !error;
});

// ============================================================
// DAILY ASSIGNMENTS (coverage)
// App format: { "2026-03-01": { teacherId: classroomId } }
// DB format: individual rows per teacher per date
// ============================================================
export const fetchDailyAssignments = async (schoolId) => {
  const { data, error } = await supabase.from("daily_assignments").select("*").eq("school_id", schoolId);
  if (error) dbError("fetch", "dailyassignments", error);
  const result = {};
  (data || []).forEach(row => {
    if (!result[row.date]) result[row.date] = {};
    result[row.date][row.teacher_id] = row.classroom_id;
  });
  return result;
};

export const syncDailyAssignments = async (assignments, schoolId) => safe("sync", "daily assignments", async () => {
  const rows = [];
  Object.entries(assignments).forEach(([date, teachers]) => {
    Object.entries(teachers).forEach(([teacherId, classroomId]) => {
      rows.push({ school_id: schoolId, date, teacher_id: String(teacherId), classroom_id: String(classroomId) });
    });
  });
  if (rows.length === 0) return;
  const { error } = await supabase.from("daily_assignments").upsert(rows, { onConflict: "school_id,teacher_id,date" });
  if (error) dbError("sync", "dailyassignments", error);
});

// ============================================================
// ACTIVITY LOG (daily log entries)
// App format: { "2026-03-01": [{ id, studentId, type, detail, time, notes }] }
// DB format: individual rows
// ============================================================
export const fetchActivityLog = async (schoolId) => {
  const { data, error } = await supabase.from("activity_log").select("*").eq("school_id", schoolId).order("created_at");
  if (error) dbError("fetch", "activitylog", error);
  const result = {};
  (data || []).forEach(row => {
    if (!result[row.date]) result[row.date] = [];
    result[row.date].push({
      id: row.id, studentId: row.student_id, type: row.type,
      detail: row.detail || "", time: row.time || "", notes: row.notes || "",
      recordedBy: row.recorded_by, photoUrl: row.photo_url || null
    });
  });
  return result;
};

export const insertActivityEntry = async (entry, date, schoolId) => safe("save", "activity entry", async () => {
  const row = {
    school_id: schoolId, student_id: String(entry.studentId), date,
    type: entry.type, detail: entry.detail || null, time: entry.time || null,
    notes: entry.notes || null, recorded_by: entry.recordedBy || null,
    photo_url: entry.photoUrl || null
  };
  const { data, error } = await supabase.from("activity_log").insert(row).select().single();
  if (error) dbError("save", "activity entry", error);
  return data ? data.id : null;
});

export const deleteActivityEntry = async (id) => safe("delete", "activity entry", async () => {
  if (!isUUID(id)) return false;
  const { error } = await supabase.from("activity_log").delete().eq("id", id);
  if (error) dbError("delete", "activityentry", error);
  return !error;
});

// ============================================================
// INVITES
// ============================================================
export const fetchInvites = async (schoolId) => {
  const { data, error } = await supabase.from("invites").select("*").eq("school_id", schoolId).order("created_at", { ascending: false });
  if (error) dbError("fetch", "invites", error);
  return (data || []).map(i => ({
    id: i.id, email: i.email, name: i.name, role: i.role,
    assignedRoom: i.assigned_room, status: i.status,
    invitedBy: i.invited_by, createdAt: i.created_at, school_id: i.school_id
  }));
};

export const createInvite = async (invite, schoolId, invitedBy) => safe("save", "invite", async () => {
  const row = {
    school_id: schoolId, email: invite.email.toLowerCase().trim(),
    name: invite.name, role: invite.role || "teacher",
    assigned_room: invite.assignedRoom || null,
    invited_by: invitedBy, status: "pending"
  };
  const { data, error } = await supabase.from("invites").upsert(row, { onConflict: "school_id,email" }).select().single();
  if (error) dbError("save", "invite", error);
  return data;
});

export const deleteInvite = async (id) => safe("delete", "invite", async () => {
  const { error } = await supabase.from("invites").delete().eq("id", id);
  if (error) dbError("delete", "invite", error);
  return !error;
});

export const checkInviteForEmail = async (email) => {
  const { data, error } = await supabase.from("invites").select("*").eq("email", email.toLowerCase().trim()).eq("status", "pending").maybeSingle();
  if (error) console.error("checkInviteForEmail:", error);
  return data;
};

export const acceptInvite = async (inviteId) => safe("save", "invite acceptance", async () => {
  const { error } = await supabase.from("invites").update({ status: "accepted" }).eq("id", inviteId);
  if (error) dbError("save", "invite acceptance", error);
  return !error;
});

// PROFILES - fetch school members
export const fetchSchoolProfiles = async (schoolId) => {
  const { data, error } = await supabase.from("profiles").select("*").eq("school_id", schoolId).order("name");
  if (error) dbError("fetch", "schoolprofiles", error);
  return (data || []).map(p => ({
    id: p.id, name: p.name, email: p.email, role: p.role,
    assignedRoom: p.assigned_room, pin: p.pin || "0000",
    linkedStudents: p.linked_students || [], status: p.status || "active",
    school_id: p.school_id, digestOptOut: p.digest_opt_out || false
  }));
};

export const updateProfile = async (profileId, updates) => safe("save", "profile", async () => {
  const row = {};
  if (updates.name !== undefined) row.name = updates.name;
  if (updates.role !== undefined) row.role = updates.role;
  if (updates.assignedRoom !== undefined) row.assigned_room = updates.assignedRoom || null;
  if (updates.pin !== undefined) row.pin = updates.pin;
  if (updates.linkedStudents !== undefined) row.linked_students = updates.linkedStudents;
  if (updates.status !== undefined) row.status = updates.status;
  const { data, error } = await supabase.from("profiles").update(row).eq("id", profileId).select().single();
  if (error) dbError("save", "profile", error);
  return data;
});

// ============================================================
// TEACHER ABSENCE REQUESTS
// ============================================================

export const fetchAbsenceRequests = async (schoolId) => {
  const { data, error } = await supabase
    .from("teacher_absence_requests")
    .select("*")
    .eq("school_id", schoolId)
    .order("created_at", { ascending: false });
  if (error) dbError("fetch", "absence requests", error);
  return (data || []).map(r => ({
    id: r.id,
    schoolId: r.school_id,
    teacherId: r.teacher_id,
    type: r.type,
    dates: r.dates || [],
    estimatedArrival: r.estimated_arrival || "",
    reason: r.reason || "",
    note: r.note || "",
    status: r.status || "active",
    createdAt: r.created_at,
  }));
};

export const submitAbsenceRequest = async (request, schoolId) => safe("save", "absence request", async () => {
  const { data, error } = await supabase
    .from("teacher_absence_requests")
    .insert({
      school_id: schoolId,
      teacher_id: request.teacherId,
      type: request.type,
      dates: request.dates,
      estimated_arrival: request.estimatedArrival || null,
      reason: request.reason || null,
      note: request.note || null,
      status: "active",
    })
    .select()
    .single();
  if (error) dbError("save", "absence request", error);
  return data;
});

export const cancelAbsenceRequest = async (requestId) => safe("cancel", "absence request", async () => {
  const { error } = await supabase
    .from("teacher_absence_requests")
    .update({ status: "cancelled" })
    .eq("id", requestId);
  if (error) dbError("cancel", "absence request", error);
  return !error;
});

// ============================================================
// ATTENDANCE SUBMISSIONS
// ============================================================

export const fetchAttendanceSubmissions = async (schoolId) => {
  const { data, error } = await supabase
    .from("attendance_submissions")
    .select("*")
    .eq("school_id", schoolId);
  if (error) dbError("fetch", "attendance submissions", error);
  return (data || []).map(r => ({
    id: r.id,
    schoolId: r.school_id,
    classroomId: r.classroom_id,
    date: r.date,
    submittedBy: r.submitted_by,
    submittedAt: r.submitted_at,
    type: r.type,
  }));
};

export const submitAttendance = async (schoolId, classroomId, date, userId, type) => safe("submit", "attendance", async () => {
  const { data, error } = await supabase
    .from("attendance_submissions")
    .upsert({
      school_id: schoolId,
      classroom_id: classroomId,
      date,
      submitted_by: userId,
      type,
    }, { onConflict: "school_id,classroom_id,date,type" })
    .select()
    .single();
  if (error) dbError("submit", "attendance", error);
  return data;
});

// ============================================================
// CHECK-IN LOG
// ============================================================

export const fetchCheckinLog = async (schoolId, date) => {
  const query = supabase
    .from("checkin_log")
    .select("*")
    .eq("school_id", schoolId)
    .order("timestamp", { ascending: false });
  if (date) query.eq("date", date);
  const { data, error } = await query;
  if (error) dbError("fetch", "checkin log", error);
  return (data || []).map(r => ({
    id: r.id,
    schoolId: r.school_id,
    studentId: r.student_id,
    direction: r.direction,
    method: r.method,
    checkedInBy: r.checked_in_by,
    timestamp: r.timestamp,
    date: r.date,
  }));
};

export const insertCheckinEntry = async (entry) => safe("save", "checkin entry", async () => {
  const { data, error } = await supabase.from("checkin_log").insert({
    school_id: entry.schoolId,
    student_id: entry.studentId,
    direction: entry.direction,
    method: entry.method || "pin",
    checked_in_by: entry.checkedInBy || null,
    timestamp: entry.timestamp || new Date().toISOString(),
    date: entry.date,
  }).select().single();
  if (error) dbError("save", "checkin entry", error);
  return data;
});

export const updateParentPin = async (profileId, pin) => safe("save", "parent PIN", async () => {
  const { error } = await supabase
    .from("profiles")
    .update({ checkin_pin: pin })
    .eq("id", profileId);
  if (error) dbError("save", "parent PIN", error);
  return !error;
});

// ─── FORECAST SCENARIOS ───────────────────────────────────────────────────────

export const fetchForecastScenarios = async (schoolId) =>
  safe("fetch", "forecast scenarios", async () => {
    const { data, error } = await supabase
      .from("forecast_scenarios")
      .select("*")
      .eq("school_id", schoolId)
      .order("created_at", { ascending: false });
    if (error) { dbError("fetch", "forecast scenarios", error); return []; }
    return (data || []).map(r => ({
      id:        r.id,
      name:      r.name,
      state:     r.state_json,
      wi:        parseFloat(r.wi_monthly),
      createdAt: r.created_at,
      createdBy: r.created_by,
    }));
  });

export const saveForecastScenario = async (scenario, schoolId, userId) =>
  safe("save", "forecast scenario", async () => {
    const { data, error } = await supabase
      .from("forecast_scenarios")
      .insert({
        school_id:  schoolId,
        created_by: userId,
        name:       scenario.name,
        state_json: scenario.state,
        wi_monthly: scenario.wi,
      })
      .select()
      .single();
    if (error) { dbError("save", "forecast scenario", error); return null; }
    return {
      id:        data.id,
      name:      data.name,
      state:     data.state_json,
      wi:        parseFloat(data.wi_monthly),
      createdAt: data.created_at,
      createdBy: data.created_by,
    };
  });

export const deleteForecastScenario = async (scenarioId) =>
  safe("delete", "forecast scenario", async () => {
    const { error } = await supabase
      .from("forecast_scenarios")
      .delete()
      .eq("id", scenarioId);
    if (error) { dbError("delete", "forecast scenario", error); return false; }
    return true;
  });
